import { Select as BaseSelect, Checkbox, Tag } from 'antd';
import { noop } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Highlighter from '../../Highlighter';
import { defaultChoiceColor } from '../utils';
import './MultipleSelect.less';
import MobileSelect from './Select/MobileSelect';

export default function MultipleSelect({
  onChange: propOnChange,
  onAfterChange = noop,
  setFieldsValueChanged = noop,
  value,
  disabled,
  colorEnabled,
  label,
  choices,
  placeholder,
  showAll = false,
  dropdownMatchSelectWidth,
  onBlur: propOnBlur,
  readWrite,
  autoFocus,
  defaultOpen,
  id,
  size,
  style,
  bordered,
  className = '',
}) {
  const [searchValue, setSearchValue] = useState('');
  const [innerChoices, setInnerChoices] = useState([]);
  const [allValue, setAllValue] = useState('0_select_all');
  const [open, setOpen] = useState(false);
  const hasChange = useRef(false);

  useEffect(() => {
    const unExistChoices =
      (value || []).filter((name) => !(choices || []).some(({ text }) => text === name)) || [];
    setInnerChoices(
      (choices || [])
        .concat(
          unExistChoices.map((text) => ({
            text,
            value: text,
            color: defaultChoiceColor,
          })),
        )
        .filter((item) => item),
    );
  }, [choices, value]);

  useEffect(() => {
    const now = new Date();
    setAllValue(`${now.getTime()}_select_all`);
  }, [choices]);

  const renderItem = ({ text, color, icon }) => {
    if (colorEnabled) {
      return (
        <Tag color={color || defaultChoiceColor} style={{ borderRadius: 11 }}>
          <Highlighter searchValue={searchValue} text={text} />
        </Tag>
      );
    }
    return <Highlighter searchValue={searchValue} text={text} />;
  };

  const renderOption = () => {
    return innerChoices.map(({ text, color, icon }) => (
      <BaseSelect.Option value={text} color={color}>
        {renderItem({ text, color, icon })}
      </BaseSelect.Option>
    ));
  };

  const propValue = useMemo(() => {
    const propValue = [...(value || [])];
    if (value?.length === innerChoices?.length && showAll) {
      propValue.push(allValue);
    }
    return propValue;
  }, [value, innerChoices, showAll, allValue]);

  const renderOptionWithAll = () => {
    return (
      <>
        <BaseSelect.Option value={allValue} label="全选">
          <Checkbox
            indeterminate={value?.length > 0 && value?.length < innerChoices?.length}
            checked={value?.length >= innerChoices?.length}
          />
          全选
        </BaseSelect.Option>
        {innerChoices.map(({ text, color, icon }) => (
          <BaseSelect.Option value={text} color={color}>
            <Checkbox checked={value?.includes(text)} />
            {renderItem({ text, color, icon })}
          </BaseSelect.Option>
        ))}
      </>
    );
  };

  const onChange = (value) => {
    hasChange.current = true;

    propOnChange(value, { doChain: !open });
    setFieldsValueChanged();
    if (searchValue) {
      setSearchValue('');
    }
  };

  const onBlur = (options) => {
    if (propOnBlur) {
      propOnBlur(options);
    }
    if (hasChange.current) {
      onAfterChange(options);
      hasChange.current = false;
    }
  };

  if (isMobile) {
    return (
      <MobileSelect
        id={id}
        size={size}
        onChange={(val, props) => {
          propOnChange(val, props);
          hasChange.current = true;
        }}
        value={value}
        colorEnabled={colorEnabled}
        allowClear={true}
        disabled={disabled}
        label={label}
        choices={choices}
        multiple
        autoFocus={autoFocus}
        defaultOpen={defaultOpen}
        onBlur={onBlur}
        showAll={showAll}
        placeholder={placeholder || `请选择${label}`}
        bordered={bordered}
      />
    );
  }

  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    if (value === allValue) {
      return null;
    }

    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    const choice = innerChoices.find((item) => item.text === value);
    const color = choice?.color || defaultChoiceColor;
    return (
      <Tag
        color={colorEnabled ? color : undefined}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={colorEnabled ? { marginRight: 3, borderRadius: 11 } : {}}
        className={`field-tag-${value}`}
      >
        <span>{value}</span>
      </Tag>
    );
  };

  return (
    <BaseSelect
      key={id}
      id={id}
      size={size}
      mode="multiple"
      style={style}
      className={`custom-field-multi-select ${showAll ? 'all' : ''} ${className || ''}`}
      dropdownClassName={`custom-field-multi-select-dropdown ${showAll ? 'all' : ''}`}
      value={propValue}
      placeholder={'请选择，支持多选'}
      allowClear
      showArrow
      autoFocus={autoFocus}
      defaultOpen={defaultOpen}
      disabled={disabled}
      showSearch
      searchValue={searchValue}
      onSearch={setSearchValue}
      autoClearSearchValue
      tagRender={colorEnabled || showAll ? tagRender : null}
      menuItemSelectedIcon={showAll ? null : undefined}
      onSelect={(val) => {
        if (val === allValue) {
          onChange([...innerChoices.map(({ text }) => text)]);
          return;
        }

        onChange([...(value || []), val]);
      }}
      onDeselect={(val) => {
        if (val === allValue) {
          onChange([]);
          return;
        }

        const index = value?.indexOf(val);
        if (index >= 0) {
          value.splice(index, 1);
        }
        onChange([...(value || [])]);
      }}
      onClear={() => onChange([])}
      onBlur={onBlur}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      onDropdownVisibleChange={setOpen}
      bordered={bordered}
    >
      {showAll ? renderOptionWithAll() : renderOption()}
    </BaseSelect>
  );
}
